import { useCallback, useEffect, useState } from "react";

import {
  getAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  setPrimaryAddress,
} from "../../api/savedAddressApi";

// ── Reusable saved-address book hook ────────────────────────────────────────
// One place for the customer address-book CRUD so any screen that needs the
// customer's addresses (checkout picker, profile manager, future flows) shares
// the same state + actions instead of re-implementing fetch/create/update.
//
// Returns: { addresses, loading, error, refresh, create, update, remove,
//            makePrimary, setError }. Mutations re-fetch so the list (and the
//            single-primary invariant the server enforces) stays consistent.

export default function useSavedAddresses({ auto = true } = {}) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAddresses();
      setAddresses((data && data.addresses) || []);
    } catch (e) {
      setError("Could not load your saved addresses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) refresh();
  }, [auto, refresh]);

  const create = useCallback(
    async (payload) => {
      const data = await createAddress(payload);
      await refresh();
      return data && data.address;
    },
    [refresh]
  );

  const update = useCallback(
    async (id, payload) => {
      const data = await updateAddress(id, payload);
      await refresh();
      return data && data.address;
    },
    [refresh]
  );

  const remove = useCallback(async (id) => {
    await deleteAddress(id);
    setAddresses((prev) => prev.filter((a) => a._id !== id));
  }, []);

  const makePrimary = useCallback(
    async (id) => {
      await setPrimaryAddress(id);
      await refresh();
    },
    [refresh]
  );

  return {
    addresses,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
    makePrimary,
    setError,
  };
}
